import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";

import { useApolloClient, useMutation } from "@apollo/client";
import { ForecastActivityType, TYPE_WELLS } from "constants/settings.constants";
import { useForecastStore } from "stores/forecast";

import { FOLDERS, RESTORE_FORECAST } from "api/userArps";

import { useSelectedProject } from "components/project/projects/hooks";

import { useForecastFolderFetcher } from "../../../arps/hooks";
import { getTypeWellUploadResync } from "../utils/TypeWellHelper";

export interface RestoreForecastRequest {
  forecastId: string;
  folderId: string;
  restoreDate: string;
}

export const useRestoreForecast = (forecastType: ForecastActivityType) => {
  const apolloClient = useApolloClient();
  const { getForecastFoldersFromFoldersData } = useForecastFolderFetcher(forecastType);

  const { selectedProject: project } = useSelectedProject();
  const [restoreForecastMutation, { loading, error }] = useMutation(RESTORE_FORECAST);

  const restoreForecast = async (RestoreForecastRequest: RestoreForecastRequest) => {
    const input = {
      forecastId: RestoreForecastRequest.forecastId,
      folderId: RestoreForecastRequest.folderId,
      restoreDate: RestoreForecastRequest.restoreDate
    };
    const foldersVariables = {
      req: { projectId: project?.projectId, type: forecastType }
    };

    await restoreForecastMutation({
      variables: { input },
      onError: (err) => {
        toast.error(err.message);
      }
    });

    // EVA-4996: a FOLDERS refetch alone only refreshes components that read FOLDERS via
    // useQuery — it does NOT refresh the Zustand working snapshots (selectedTypeWell /
    // checkedGlobalTypeWells / checkedTypeWells) that the editor and plotted curve read from.
    // Left alone, a restore would leave stale constants from the previously-open type well in
    // the working view until it was re-selected. So instead of relying on refetchQueries, fetch
    // the fresh folders ourselves (network-only, which also updates the Apollo cache for any
    // other FOLDERS consumers — the same effect refetchQueries would have had) and resync those
    // snapshots from the result — mirroring the EVA-6256 upload resync in ProjectForecasts.tsx
    // (getTypeWellUploadResync) — so the restored version's ACTUAL persisted constants (empty
    // OR non-empty) flow into the working view, rather than guessing at a value here.
    //
    // This fetch+resync is wrapped in its own try/catch: it runs after the mutation has already
    // succeeded (the restore itself is safely persisted server-side), so a network/GraphQL
    // failure here must not throw an unhandled rejection back at the caller (RestoreForecastButton
    // calls restore() un-awaited) — surface it via toast + Sentry instead and move on.
    if (forecastType === TYPE_WELLS) {
      try {
        const { data: foldersData } = await apolloClient.query({
          query: FOLDERS,
          variables: foldersVariables,
          fetchPolicy: "network-only"
        });
        if (foldersData) {
          const folders = getForecastFoldersFromFoldersData(foldersData, forecastType);
          const store = useForecastStore.getState();
          const resync = getTypeWellUploadResync(
            folders,
            store.selectedTypeWell,
            // Unlike the EVA-6256 upload resync, a restore is the user explicitly discarding
            // working state to revert to a prior version — their unsaved edits (if any) are
            // meant to be replaced, not preserved. So bypass the "don't clobber unsaved
            // changes" guard here by always passing hasUnsavedChanges=false, regardless of the
            // actual store value.
            false,
            store.checkedGlobalTypeWells,
            store.checkedTypeWells
          );
          if (resync) {
            store.setSelectedTypeWell(resync.selectedTypeWell);
            store.setCheckedGlobalTypeWells(resync.checkedGlobalTypeWells);
            store.setCheckedTypeWells(resync.checkedTypeWells);
            // The working view is now fully in sync with the server-persisted restored
            // state, so clear the dirty flag. Since restore bypasses the hasUnsavedChanges
            // guard above, any prior unsaved edits are intentionally discarded — leaving the
            // flag set would trigger a false "unsaved changes" warning on the next type-well
            // switch (and could revert the just-restored view to the pre-edit snapshot).
            store.setHasUnsavedChanges(false);
          }
        }
      } catch (err) {
        toast.error(
          "Forecast restored, but the working view failed to refresh. Please reselect the well to see the latest data."
        );
        Sentry.captureException(err, {
          tags: { feature: "forecasts", operation: "restoreForecastResync" },
          extra: {
            forecastId: input.forecastId,
            folderId: input.folderId,
            restoreDate: input.restoreDate,
            forecastType
          }
        });
      }
    }
  };

  return { restore: restoreForecast, loading, error };
};
